import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { defer, finalize, Observable, tap } from 'rxjs';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import { DfService } from '../../../../core/df/df.service';
import {
  DfFacilityOps,
  DfFacilitySubtype,
  InsuranceStatusPayload,
} from '../../../../core/df/df.types';
import { OpsEmailLogEntry } from '../../../../core/status/email-log.types';
import { EmailTrailComponent } from '../../../../shared/email-trail/email-trail';
import { StatusChipComponent } from '../../../../shared/status-chip/status-chip';
import { IconComponent } from '../../../../shared/icon/icon';
import { AssignmentPanelComponent } from '../../../../shared/assignment-panel/assignment-panel';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from '../../../../shared/confirm-dialog/confirm-dialog';
import { ZarPipe } from '../../../../shared/pipes/zar.pipe';
import { DashPipe } from '../../../../shared/pipes/dash.pipe';
import { RiskSummaryPanelComponent } from '../../../../shared/risk-summary-panel/risk-summary-panel';
import { CcResolutionPanelComponent } from '../../../../shared/cc-resolution-panel/cc-resolution-panel';
import { SizingHoldCardComponent } from '../../../../shared/sizing-hold-card/sizing-hold-card';
import { OpsRecoveryFlagComponent } from '../../../../shared/ops-recovery-flag/ops-recovery-flag';
import { EntityVerificationPanelComponent } from '../../../../shared/entity-verification-panel/entity-verification-panel';
import { DeclarationsPanelComponent } from '../../../../shared/declarations-panel/declarations-panel';
import { AiAdvisoryPanelComponent } from '../../../../shared/ai-advisory-panel/ai-advisory-panel';
import { AiNotesComponent } from '../../../../shared/ai-notes/ai-notes';
import { AiComparisonPanelComponent } from '../../../../shared/ai-comparison-panel/ai-comparison-panel';
import { AiCostPanelComponent } from '../../../../shared/ai-cost-panel/ai-cost-panel';
import { AiCallService } from '../../../../core/admin/ai-call.service';
import { AiCostBreakdown } from '../../../../core/admin/ai-cost.types';
import { InsurancePanelComponent } from './insurance-panel/insurance-panel';
import { AiConsentOpsRecordComponent } from '../../../../shared/ai-consent/ai-consent-ops-record/ai-consent-ops-record';
import { readError } from '../../../../core/http/read-error';

@Component({
  selector: 'app-df-facility-ops-detail',
  imports: [
    AssignmentPanelComponent,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    RouterLink,
    DatePipe,
    StatusChipComponent,
    IconComponent,
    ZarPipe,
    DashPipe,
    EmailTrailComponent,
    RiskSummaryPanelComponent,
    CcResolutionPanelComponent,
    OpsRecoveryFlagComponent,
    SizingHoldCardComponent,
    EntityVerificationPanelComponent,
    DeclarationsPanelComponent,
    AiAdvisoryPanelComponent,
    AiComparisonPanelComponent,
    AiCostPanelComponent,
    AiNotesComponent,
    AiConsentOpsRecordComponent,
    InsurancePanelComponent,
  ],
  templateUrl: './df-facility-ops-detail.html',
  styleUrl: './df-facility-ops-detail.scss',
  providers: [ZarPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DfFacilityOpsDetailComponent implements OnInit {
  private readonly dfService = inject(DfService);
  private readonly route = inject(ActivatedRoute);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zar = inject(ZarPipe);
  private readonly aiCallService = inject(AiCallService);

  private readonly facilityId = this.route.snapshot.paramMap.get('facilityId') ?? '';

  protected readonly facility = signal<DfFacilityOps | null>(null);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);
  protected readonly isActioning = signal(false);
  protected readonly isAssessing = signal(false);
  protected readonly serverError = signal<string | null>(null);
  /** A declared-account hold release in flight, and its failure (FLOW-283). */
  protected readonly emails = signal<OpsEmailLogEntry[]>([]);
  protected readonly emailsLoading = signal(false);
  protected readonly aiCost = signal<AiCostBreakdown | null>(null);
  protected readonly aiCostLoading = signal(false);

  readonly subtypes: DfFacilitySubtype[] = ['FACTORING', 'SID', 'HYBRID'];

  /** Display labels for the DF facility sub-types (never raw enums to users). */
  protected readonly subtypeLabels: Record<DfFacilitySubtype, string> = {
    FACTORING: 'Factoring',
    SID: 'Selective Invoice Discounting',
    HYBRID: 'Hybrid',
  };

  /** The cc_ref minted by a passed Credit Committee resolution, if any. */
  protected readonly passedCcRef = signal<string | null>(null);

  protected readonly approveForm = this.fb.group({
    facility_subtype: ['FACTORING' as DfFacilitySubtype, Validators.required],
    facility_limit: ['', [Validators.required, Validators.min(1)]],
    cc_ref: [''],
    note: [''],
  });

  protected readonly declineForm = this.fb.group({
    reason: ['', Validators.required],
    note: [''],
  });

  /**
   * Whether this facility needs a Credit Committee reference. Above the amount
   * gate the requested OR approved limit crosses R1m, so we key off whichever is
   * known — the requested limit is set from submission, the form limit from the
   * reviewer. This drives the cc_ref required validator.
   */
  protected readonly needsCc = computed(() => {
    const requested = parseFloat(this.facility()?.requested_limit ?? '0');
    return requested > 1_000_000;
  });

  /** Whole days since submission, for a header triage signal. */
  protected readonly daysSinceSubmission = computed<number | null>(() => {
    const submitted = this.facility()?.submitted_at;
    if (!submitted) return null;
    const ms = Date.now() - new Date(submitted).getTime();
    return Math.max(0, Math.floor(ms / 86_400_000));
  });

  /** True once a Credit Committee resolution has passed for this facility. */
  protected readonly committeeCleared = computed(() => this.passedCcRef() !== null);

  /**
   * Empty debtor book where a book is expected (requested limit set, nothing
   * captured): flag-don't-guess — tell the reviewer it may be uncaptured rather
   * than letting a silent zero read as "genuinely empty".
   */
  protected readonly debtorBookMaybeUncaptured = computed(() => {
    const f = this.facility();
    if (!f) return false;
    const fields = f.risk_summary?.fields ?? [];
    const debtors = fields.find((x) => x.key === 'debtor_count')?.value;
    return Number(debtors ?? 0) === 0 && f.application_stage === 'SUBMITTED';
  });

  /** Receive the passed cc_ref from the CC panel and pre-fill the approve form. */
  protected onPassedRef(ref: string | null): void {
    this.passedCcRef.set(ref);
    if (ref && !this.approveForm.controls.cc_ref.value) {
      this.approveForm.controls.cc_ref.setValue(ref);
    }
    const ctrl = this.approveForm.controls.cc_ref;
    if (this.needsCc()) {
      ctrl.addValidators(Validators.required);
    } else {
      ctrl.removeValidators(Validators.required);
    }
    ctrl.updateValueAndValidity({ emitEvent: false });
    this.cdr.markForCheck();
  }

  ngOnInit(): void {
    this.load();
    this.loadEmails();
    this.loadAiCost();
  }

  private loadAiCost(): void {
    this.aiCostLoading.set(true);
    this.aiCallService
      .costForApplication('df', this.facilityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (breakdown) => {
          this.aiCost.set(breakdown);
          this.aiCostLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.aiCostLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  protected load(): void {
    this.dfService
      .opsGetFacility(this.facilityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (f) => {
          this.facility.set(f);
          this.isLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.cdr.markForCheck();
        },
      });
  }

  private loadEmails(): void {
    this.emailsLoading.set(true);
    this.dfService
      .opsGetFacilityEmails(this.facilityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (entries) => {
          this.emails.set(entries);
          this.emailsLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.emailsLoading.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  protected approve(): void {
    if (this.approveForm.invalid) {
      this.approveForm.markAllAsTouched();
      return;
    }
    const v = this.approveForm.getRawValue();
    const subtypeLabel = v.facility_subtype ? this.subtypeLabels[v.facility_subtype] : 'facility';
    const limit = this.zar.transform(v.facility_limit);
    const data: ConfirmDialogData = {
      title: 'Approve this facility?',
      message:
        `Approving activates a ${subtypeLabel} facility with a limit of ${limit} ` +
        `for ${this.facility()?.entity_name}. This cannot be undone.`,
      confirmLabel: 'Approve',
    };
    this.confirmAndDecide(
      data,
      {
        action: 'approve',
        facility_subtype: v.facility_subtype as DfFacilitySubtype,
        facility_limit: v.facility_limit ?? undefined,
        cc_ref: v.cc_ref ?? undefined,
        note: v.note ?? undefined,
      },
      this.approveForm,
    );
  }

  protected decline(): void {
    if (this.declineForm.invalid) {
      this.declineForm.markAllAsTouched();
      return;
    }
    const v = this.declineForm.getRawValue();
    const data: ConfirmDialogData = {
      title: 'Decline this facility?',
      message: 'This will decline the application.',
      confirmLabel: 'Decline',
      destructive: true,
    };
    this.confirmAndDecide(
      data,
      {
        action: 'decline',
        reason: v.reason ?? undefined,
        note: v.note ?? undefined,
      },
      this.declineForm,
    );
  }

  private confirmAndDecide(
    data: ConfirmDialogData,
    payload: Parameters<DfService['opsFacilityDecision']>[1],
    form: FormGroup,
  ): void {
    this.confirmAndRun(
      data,
      () => this.dfService.opsFacilityDecision(this.facilityId, payload),
      'Decision recorded.',
      form,
    );
  }

  /**
   * The dialog owns the write, so a failure keeps the dialog and its input;
   * only a completed write closes it (Tier 2 §4b).
   */
  private confirmAndRun(
    data: ConfirmDialogData,
    write: () => Observable<DfFacilityOps>,
    message: string,
    form?: FormGroup,
  ): void {
    this.dialog.open(ConfirmDialogComponent, {
      width: '440px',
      data: {
        ...data,
        confirm: () =>
          defer(() => {
            this.isActioning.set(true);
            this.serverError.set(null);
            this.cdr.markForCheck();
            return write().pipe(
              tap((f) => {
                this.facility.set(f);
                this.snackBar.open(message, 'Dismiss', { duration: 5000 });
              }),
              finalize(() => {
                this.isActioning.set(false);
                this.cdr.markForCheck();
              }),
            );
          }),
        errorMessage: (err: unknown) => readError(err),
        resetOnClose: () => form?.reset(),
      } satisfies ConfirmDialogData,
    });
  }

  /**
   * BRE-200 DF-OB-07 — confirm cession of the debtor book / TPFA.
   *
   * Not routed through ``confirmAndDecide``: that helper posts to the decision
   * endpoint, and this is a standalone confirmation with its own endpoint. It
   * is also not a decision — it records that a precondition of activation is
   * met, which is why it stays available before any approve/decline.
   *
   * The dialog spells out the competing-cession limb because nothing in the
   * system can check it: BRE-200 §4 requires existing cessions to other funders
   * be cancelled or waived first, and that is the officer's own attestation.
   */
  protected confirmCession(): void {
    if (this.isActioning()) return;

    const data: ConfirmDialogData = {
      title: 'Confirm cession of the debtor book?',
      message:
        'Confirm that cession of the debtor book to CapX (or a TPFA arrangement) ' +
        'is in place, and that any existing cessions to other funders have been ' +
        'cancelled or waived. This is recorded against your name.',
      confirmLabel: 'Confirm cession',
    };

    this.confirmAndRun(
      data,
      () => this.dfService.opsConfirmCession(this.facilityId),
      'Cession confirmed.',
    );
  }

  protected rerunAssessment(): void {
    if (this.isAssessing()) return;
    this.isAssessing.set(true);
    this.serverError.set(null);

    this.dfService
      .opsFacilityAssess(this.facilityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.isAssessing.set(false);
          this.snackBar.open(res.summary, 'Dismiss', { duration: 6000 });
          this.load();
        },
        error: (err: unknown) => {
          this.isAssessing.set(false);
          this.serverError.set(readError(err));
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Release the declared-account hold on this facility (FLOW-283).
   *
   * Nothing is re-scored: this lane has no automated sizing (`§A12.4`), which
   * is why the hold lands on the assessor rather than on the engine. Releasing
   * lets the next approve through the offer guard — it does not itself make an
   * offer, and no figure appears as a result.
   */
  protected readonly releaseHold = (releaseNote: string): Observable<unknown> =>
    this.dfService
      .opsReleaseSizingHold(this.facilityId, releaseNote)
      .pipe(tap((facility) => this.facility.set(facility)));

  /** `§C7` — set the business's insurance status; the dialog closes only on success. */
  protected readonly saveInsurance = (payload: InsuranceStatusPayload): Observable<unknown> =>
    this.dfService
      .opsSetInsuranceStatus(this.facilityId, payload)
      .pipe(tap((facility) => this.facility.set(facility)));
}
